import { Security, Speed, TrendingUp } from "@mui/icons-material";
import {
  Box,
  Container,
  Fade,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const HeroSectionAbout: React.FC = () => {
  const theme = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t: myTranslate } = useTranslation();

  const heroImages = [
    "https://m.media-amazon.com/images/I/71NukmNYUyL._AC_UY327_FMwebp_QL65_.jpg",
    "https://m.media-amazon.com/images/I/81uXsYPCm8L._AC_UL480_FMwebp_QL65_.jpg",
    "https://m.media-amazon.com/images/I/81SN4e0tTpL._AC_UL480_FMwebp_QL65_.jpg",
  ];

  const features = [
    {
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      title: myTranslate("about.qualityProducts"),
      description: myTranslate("about.qualityProductsDesc"),
    },
    {
      icon: <Security sx={{ fontSize: 40 }} />,
      title: myTranslate("about.secureShopping"),
      description: myTranslate("about.secureShoppingDesc"),
    },
    {
      icon: <Speed sx={{ fontSize: 40 }} />,
      title: myTranslate("about.fastDelivery"),
      description: myTranslate("about.fastDeliveryDesc"),
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <Box
      sx={{
        minHeight: "80vh",
        background: "transparent",
        display: "flex",
        alignItems: "center",
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4} alignItems="center">
          {/* Left Content */}
          <Grid item xs={12} md={6}>
            <Box sx={{ pr: { md: 4 } }}>
              <Typography
                variant="h1"
                className="notranslate"
                translate="no"
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 3,
                  lineHeight: 1.2,
                }}
              >
                {myTranslate("about.title")}
                <Typography
                  component="span"
                  className="notranslate"
                  translate="no"
                  sx={{
                    color: theme.palette.warning.main,
                    display: "block",
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                    fontWeight: 400,
                    mt: 1,
                  }}
                >
                  {myTranslate("about.subtitle")}
                </Typography>
              </Typography>

              <Typography
                variant="body1"
                className="notranslate"
                translate="no"
                sx={{
                  color: theme.palette.text.secondary,
                  mb: 4,
                  fontSize: { xs: "0.95rem", sm: "1rem", md: "1.1rem" },
                  lineHeight: 1.6,
                  maxWidth: "90%",
                }}
              >
                {myTranslate("about.fullDescription")}
              </Typography>

              {/* Feature Highlights */}
              <Box sx={{ mt: 4 }}>
                {features.map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 2,
                      opacity: 0,
                      animation: `slideInLeft 0.6s ease-out ${index * 0.2}s forwards`,
                    }}
                  >
                    <Box
                      sx={{
                        color: theme.palette.warning.main,
                        mr: 2,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        className="notranslate"
                        translate="no"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          fontSize: { xs: "0.95rem", sm: "1rem" },
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        className="notranslate"
                        translate="no"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: { xs: "0.85rem", sm: "0.9rem" },
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Right Image Slider */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: "relative",
                height: { xs: 300, md: 500 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {/* Main Image Display */}
              <Paper
                elevation={8}
                sx={{
                  width: "85%",
                  height: "85%",
                  borderRadius: 4,
                  overflow: "hidden",
                  position: "relative",
                  background: "white",
                }}
              >
                {heroImages.map((image, index) => (
                  <Fade key={index} in={currentSlide === index} timeout={1000}>
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 4,
                      }}
                    >
                      <Box
                        component="img"
                        src={image}
                        alt={`Hero ${index + 1}`}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  </Fade>
                ))}

                {/* Slide Indicators */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    gap: 1,
                  }}
                >
                  {heroImages.map((_, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor:
                          currentSlide === index
                            ? theme.palette.warning.main
                            : theme.palette.grey[300],
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: theme.palette.warning.light,
                        },
                      }}
                    />
                  ))}
                </Box>
              </Paper>

              {/* Floating Cards */}
              <Paper
                elevation={4}
                sx={{
                  position: "absolute",
                  top: "10%",
                  right: "-5%",
                  p: 2,
                  borderRadius: 2,
                  background: "white",
                  animation: "float 3s ease-in-out infinite",
                }}
              >
                <Typography
                  variant="body2"
                  className="notranslate"
                  translate="no"
                  sx={{ fontWeight: 600 }}
                >
                  {myTranslate("about.floatingCard1Title")}
                </Typography>
                <Typography
                  variant="caption"
                  className="notranslate"
                  translate="no"
                  color="text.secondary"
                >
                  {myTranslate("about.floatingCard1Subtitle")}
                </Typography>
              </Paper>

              <Paper
                elevation={4}
                sx={{
                  position: "absolute",
                  bottom: "15%",
                  left: "-10%",
                  p: 2,
                  borderRadius: 2,
                  background: "white",
                  animation: "float 3s ease-in-out infinite 1.5s",
                }}
              >
                <Typography
                  variant="body2"
                  className="notranslate"
                  translate="no"
                  sx={{ fontWeight: 600 }}
                >
                  {myTranslate("about.floatingCard2Title")}
                </Typography>
                <Typography
                  variant="caption"
                  className="notranslate"
                  translate="no"
                  color="text.secondary"
                >
                  {myTranslate("about.floatingCard2Subtitle")}
                </Typography>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Custom Animations */}
      <style>
        {`
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default HeroSectionAbout;
